import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { MessageCircle, Pencil, Trash2, LogOut, User, ShoppingBag, LogIn } from "lucide-react";
import { EditShopItemDialog } from "@/components/EditShopItemDialog";
import { ShopMessageThread } from "@/components/ShopMessageThread";
import { useNavigate, Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CookieBanner } from "@/components/CookieBanner";

export default function MyListings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [ptaId, setPtaId] = useState<string | null>(null);
  const [ptaData, setPtaData] = useState<any>(null);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [messageCounts, setMessageCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (user) {
      fetchPtaId();
    }
  }, [user]);

  useEffect(() => {
    if (user && ptaId) {
      fetchListings();
    }
  }, [user, ptaId]);

  useEffect(() => {
    if (ptaId) {
      fetchPTAData();
      fetchCategories();
    }
  }, [ptaId]);

  useEffect(() => {
    if (listings.length > 0 && user) {
      fetchMessageCounts();
    }
  }, [listings, user]);

  const fetchCategories = async () => {
    if (!ptaId) return;
    const { data } = await supabase
      .from('shop_categories')
      .select('*')
      .eq('pta_id', ptaId)
      .order('display_order');
    setCategories(data || []);
  };

  const fetchMessageCounts = async () => {
    if (!user || listings.length === 0) return;
    
    const itemIds = listings.map(l => l.id);
    const { data } = await supabase
      .from('shop_messages')
      .select('shop_item_id, is_read')
      .in('shop_item_id', itemIds)
      .neq('sender_user_id', user.id)
      .eq('is_read', false);

    if (data) {
      const counts: Record<string, number> = {};
      data.forEach(msg => {
        counts[msg.shop_item_id] = (counts[msg.shop_item_id] || 0) + 1;
      });
      setMessageCounts(counts);
    }
  };

  const fetchPtaId = async () => {
    if (!user) return;
    
    // Get parent ID and PTA ID
    const { data: parentData } = await supabase
      .from('parents')
      .select('id, pta_id')
      .eq('user_id', user.id)
      .single();

    if (parentData) {
      setPtaId(parentData.pta_id);
    }
  };

  const fetchPTAData = async () => {
    if (!ptaId) return;
    
    const { data } = await supabase
      .from('ptas')
      .select('*')
      .eq('id', ptaId)
      .single();
    
    if (data) {
      setPtaData(data);
    }
  };

  const fetchListings = async () => {
    if (!user) return;

    setLoading(true);
    
    // Get parent ID
    const { data: parentData } = await supabase
      .from('parents')
      .select('id')
      .eq('user_id', user.id)
      .single();

    if (!parentData) {
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('shop_items')
      .select('*')
      .eq('seller_parent_id', parentData.id)
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: "Error loading listings",
        description: error.message,
        variant: "destructive"
      });
    } else {
      setListings(data || []);
    }
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this listing?")) return;

    const { error } = await supabase
      .from('shop_items')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: "Error deleting listing",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({ title: "Listing deleted successfully" });
      fetchListings();
    }
  };

  const handleMarkSold = async (id: string) => {
    const { error } = await supabase
      .from('shop_items')
      .update({ status: 'sold' })
      .eq('id', id);

    if (error) {
      toast({
        title: "Error updating listing",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({ title: "Item marked as sold" });
      fetchListings();
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate(`/fundraising/${ptaId}`);
  };

  const getStatusBadge = (item: any) => {
    if (item.status === 'sold') {
      return <Badge variant="secondary">Sold</Badge>;
    }
    if (item.approval_status === 'pending') {
      return <Badge variant="outline">Pending Approval</Badge>;
    }
    if (item.approval_status === 'rejected') {
      return <Badge variant="destructive">Rejected</Badge>;
    }
    if (item.expires_at && new Date(item.expires_at) < new Date()) {
      return <Badge variant="secondary">Expired</Badge>;
    }
    return <Badge className="bg-green-500">Active</Badge>;
  };

  const logoUrl = ptaData?.logo_url;

  return (
    <div className="min-h-screen bg-background">
      {/* Header matching FundraisingPage */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-8">
            {/* Logo */}
            <Link to={`/fundraising/${ptaId}`} className="flex items-center gap-2">
              {logoUrl ? (
                <img src={logoUrl} alt="PTA Logo" className="h-12 w-auto rounded object-cover" />
              ) : (
                <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                  <span className="text-lg">🏫</span>
                </div>
              )}
              <span className="font-semibold text-lg hidden sm:inline-block">
                {ptaData?.name || 'PTA Marketplace'}
              </span>
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <Link to={`/fundraising/${ptaId}`} className="text-sm font-medium hover:text-primary transition-colors">
                Home
              </Link>
              <Link to={`/marketplace?pta=${ptaId}`} className="text-sm font-medium hover:text-primary transition-colors">
                Marketplace
              </Link>
            </nav>
          </div>

          {/* User Actions */}
          <div className="flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user.user_metadata?.avatar_url} alt={user.email} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {user.email?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium text-sm">{user.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(`/parent-portal/${ptaId}`)}>
                    <User className="mr-2 h-4 w-4" />
                    My Account
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(`/marketplace?pta=${ptaId}`)}>
                    <ShoppingBag className="mr-2 h-4 w-4" />
                    Marketplace
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => navigate('/auth')}>
                <LogIn className="mr-2 h-4 w-4" />
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container py-8">
        <div className="space-y-6 animate-fade-in">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">My Listings</h1>
              <p className="text-muted-foreground mt-2">Manage your marketplace items</p>
            </div>
            <Button onClick={() => navigate('/marketplace/create')}>
              Create New Listing
            </Button>
          </div>

      <Card>
        <CardHeader>
          <CardTitle>Your Items</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Loading listings...</p>
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">You haven't listed any items yet</p>
              <Button 
                onClick={() => navigate('/marketplace/create')}
                className="mt-4"
              >
                Create Your First Listing
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listings.map((listing) => (
                  <TableRow key={listing.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {listing.images && listing.images[0] ? (
                          <img 
                            src={listing.images[0]} 
                            alt={listing.name}
                            className="w-12 h-12 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
                            📦
                          </div>
                        )}
                        <div>
                          <div className="font-medium">{listing.name}</div>
                          <div className="text-sm text-muted-foreground line-clamp-1">
                            {listing.description}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{listing.category}</TableCell>
                    <TableCell>
                      {listing.condition ? (
                        <Badge variant="outline" className="capitalize text-xs">
                          {listing.condition.replace('_', ' ')}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{getStatusBadge(listing)}</TableCell>
                    <TableCell>
                      {new Date(listing.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 items-center">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 relative"
                          onClick={() => setSelectedItemId(listing.id)}
                        >
                          <MessageCircle className="h-4 w-4" />
                          {messageCounts[listing.id] > 0 && (
                            <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full h-4 w-4 flex items-center justify-center font-medium">
                              {messageCounts[listing.id]}
                            </span>
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => setEditingItem(listing)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => handleDelete(listing.id)}
                        >
                          <Trash2 className="h-4 w-4 text-foreground" />
                        </Button>
                        {listing.approval_status === 'approved' && listing.status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMarkSold(listing.id)}
                          >
                            Mark Sold
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {selectedItemId && user && (
        <ShopMessageThread
          itemId={selectedItemId}
          itemName="Item"
          open={!!selectedItemId}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedItemId(null);
              fetchMessageCounts();
            }
          }}
        />
      )}

      {editingItem && (
        <EditShopItemDialog
          item={editingItem}
          open={!!editingItem}
          onOpenChange={(open) => !open && setEditingItem(null)}
          onSuccess={() => {
            fetchListings();
            setEditingItem(null);
          }}
          categories={categories}
        />
      )}
        </div>
      </div>

      {/* Footer matching FundraisingPage */}
      <footer className="bg-muted/30 border-t border-border mt-16">
        <div className="container py-12">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-3">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to={`/fundraising/${ptaId}`} className="text-muted-foreground hover:text-primary transition-colors">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to={`/marketplace?pta=${ptaId}`} className="text-muted-foreground hover:text-primary transition-colors">
                    Marketplace
                  </Link>
                </li>
              </ul>
            </div>

            {/* Get Involved */}
            <div>
              <h4 className="font-semibold mb-3">Get Involved</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to={`/fundraising/${ptaId}`} className="text-muted-foreground hover:text-primary transition-colors">
                    Donate
                  </Link>
                </li>
                <li>
                  <Link to={`/become-a-member/${ptaId}`} className="text-muted-foreground hover:text-primary transition-colors">
                    Become a Member
                  </Link>
                </li>
              </ul>
            </div>

            {/* Policies */}
            <div>
              <h4 className="font-semibold mb-3">Policies</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/cookies" className="text-muted-foreground hover:text-primary transition-colors">
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <Link to="/refund" className="text-muted-foreground hover:text-primary transition-colors">
                    Refund Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-3">Contact</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {ptaData?.shared_email && <li>{ptaData.shared_email}</li>}
                {ptaData?.charity_number && <li>Charity: {ptaData.charity_number}</li>}
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} {ptaData?.name || 'PTA'}. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
}
